import type { GatewayResponse, ValidationIssue } from '@core/services/api-response.types'

/**
 * Application meaning carried inside the Black Box. No HTTP detail belongs
 * here — but each code corresponds to exactly one backend status, and that
 * mapping is itself meaningful: it tells you *how far a failed request got*
 * through the backend's pipeline before it failed. Earliest possible failure
 * first:
 *
 * | code                      | status | reached                                          |
 * |---------------------------|--------|---------------------------------------------------|
 * | `validation`              | 422    | request parsing — never reached business logic    |
 * | `not-found`                | 404    | a lookup, whose result was empty                  |
 * | `conflict`                | 409    | a business rule (e.g. a uniqueness check)          |
 * | `unauthorized`            | 403    | an authorization check, past authentication — you are who you say, just not allowed here |
 * | `temporarily-unavailable` | 503    | an external call (our DB, a dependency) — furthest before failing on something outside our own code |
 * | `rejected`                | other  | any other server-authored rejection (401, or an unexpected 500 — the request got the furthest of all, into truly unanticipated territory) |
 * | `invalid-external-data`   | n/a    | mechanism-level: the response body itself didn't parse/validate |
 * | `cancelled`               | n/a    | mechanism-level: no request ever completed at all |
 * | `offline`                 | n/a    | mechanism-level: the request never left the device — the network transport threw |
 *
 * No separate `stage`/`reachedTier` field carries this on the wire — status
 * already encodes it 1:1 for every case above, so a second field would only
 * ever repeat what the status already said. This table is the documentation
 * of that mapping, not a data structure backing it. A backend admin can pull
 * the real failure this collapsed from via `GET /admin/errors/:requestId`
 * (backend/src/routes/admin.routes.ts) for the two furthest-reaching cases
 * (`temporarily-unavailable`'s 503, `rejected`'s 500) — see
 * backend/src/error-log.ts.
 */
export type ActionFailurePm =
  | { code: 'rejected'; message: string }
  | { code: 'validation'; message: string; issues: readonly ValidationIssue[] }
  | { code: 'not-found'; message: string; resource: string; id: string }
  | { code: 'conflict'; message: string }
  | { code: 'unauthorized'; message: string }
  | { code: 'temporarily-unavailable' }
  | { code: 'invalid-external-data' }
  | { code: 'cancelled' }
  | { code: 'offline' }

export type ActionResponsePm = { success: true } | { success: false; failure: ActionFailurePm }

export class MessagePacking {
  static unpackGatewayResponseToPm(response: GatewayResponse): ActionResponsePm {
    if (response.success) return { success: true }

    if (response.failure.kind === 'rejected') {
      const { status, message, issues, notFound } = response.failure
      if (status === 422 && issues && issues.length > 0) {
        return { success: false, failure: { code: 'validation', message, issues } }
      }
      if (status === 404 && notFound) {
        return { success: false, failure: { code: 'not-found', message, resource: notFound.resource, id: notFound.id } }
      }
      if (status === 409) {
        return { success: false, failure: { code: 'conflict', message } }
      }
      if (status === 403) {
        return { success: false, failure: { code: 'unauthorized', message } }
      }
      return {
        success: false,
        failure: { code: 'rejected', message },
      }
    }

    if (response.failure.kind === 'cancelled') return this.cancelled()

    if (response.failure.reason === 'invalid-response') return this.invalidExternalData()
    // A thrown fetch — the request never left the device. Distinct from a 5xx
    // (`temporarily-unavailable`, the server answered), because only this case
    // means "you are offline" and only this case is what the read cache serves on.
    if (response.failure.reason === 'network') return this.offline()
    return { success: false, failure: { code: 'temporarily-unavailable' } }
  }

  static succeeded(): ActionResponsePm {
    return { success: true }
  }

  static invalidExternalData(): ActionResponsePm {
    return { success: false, failure: { code: 'invalid-external-data' } }
  }

  static cancelled(): ActionResponsePm {
    return { success: false, failure: { code: 'cancelled' } }
  }

  static offline(): ActionResponsePm {
    return { success: false, failure: { code: 'offline' } }
  }
}
